import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useSortable } from '@dnd-kit/react/sortable';

import { Project, Task } from '../../../../app/entities';
import { StatusSelector } from '../../../process/components/status-selector';
import { PriorityIcon } from '../../../process/components/priority-selector';
import { AssigneeSelector } from '../../../../components/assignee-selector/assignee-selector';
import { TaskDateSelector } from '../task-date-selector';
import { TagsViewer } from '../tags-viewer';

type TaskCardProps = {
  taskId: string;
  index: number;
  columnId: string;
  dragDisabled?: boolean;

  /**
   * Override the default navigation when clicking on the card
   */
  onSelect?: (taskId: string) => void;
};

/**
 * Read-only summary card for the kanban board. Mirrors the DnD wiring of
 * `step-item.tsx` and reuses the visual bits of `task-item.tsx`. Clicking the
 * card opens the task detail (same behaviour as the list row).
 */
export const TaskCard: React.FC<TaskCardProps> = observer(
  ({ taskId, index, columnId, dragDisabled, onSelect }) => {
    const navigate = useNavigate();
    const task = Task.getOne(taskId) as Task;

    // When drag is disabled (tag grouping) a task can appear in several columns,
    // so the sortable id must be unique per column to avoid registry collisions.
    // Draggable groupings always map a task to a single column, so the plain
    // task id is kept there — it must match the id stored in the board state for
    // `move()` to work.
    const { ref, isDragging } = useSortable({
      id: dragDisabled ? `${columnId}:${taskId}` : taskId,
      index,
      type: 'item',
      accept: 'item',
      group: columnId,
      disabled: dragDisabled,
    });

    if (!task) {
      return null;
    }

    const taskProject = task.projectId ? Project.getOne(task.projectId) : undefined;

    const handleClick = () => {
      if (onSelect) {
        onSelect(task._id);
      } else {
        navigate(`./${task._id}`);
      }
    };

    return (
      <Container ref={ref} onClick={handleClick} data-dragging={isDragging}>
        <Header>
          <StatusSelector status={task.status} statusMap={Task.StatusMapper} isViewOnly disableTooltip />

          <Title>{task.title}</Title>

          <PriorityIcon priority={task.priority} />
        </Header>

        <Footer>
          <Left>
            <AssigneeSelector
              isViewOnly
              project={taskProject}
              selectedMemberId={task.assigneeId}
              onChange={() => undefined}
              iconOnly
            />

            <TaskDateSelector
              isViewOnly
              date={task.dueDate}
              status={task.status}
              onUpdate={() => undefined}
            />
          </Left>

          <TagsViewer targetId={task._id} targetType="task" overflow="collapse" alignment="end" />
        </Footer>
      </Container>
    );
  },
);

const Container = styled.div`
  width: 100%;
  border-radius: var(--card-border-radius);
  background-color: var(--color-surface-tertiary);
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  padding: 0.8rem;
  position: relative;
  cursor: pointer;
  user-select: none;

  &:not(:last-child) {
    margin-bottom: 0.8rem;
  }

  &[data-dragging='true'] {
    opacity: 0.5;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.8rem;
`;

const Title = styled.p`
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 1.3rem;
  font-weight: 400;
  line-height: 1.6rem;
  color: var(--color-texts-high-contrast);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
`;

const Left = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.4rem;
`;
